import React, { useEffect, useRef, useState } from "react";
import Styles from "./ChatAssistant.module.css";
import { useAuth } from "../auth";
import {
  AdkEvent,
  createChatSession,
  streamAgentReply,
} from "../chat/ocAnalystApi";

type ChatRole = "user" | "agent" | "error";

type ChatMessage = {
  id: number;
  role: ChatRole;
  text: string;
};

let messageIdCounter = 0;
const nextMessageId = (): number => ++messageIdCounter;

const URL_PATTERN = /(https?:\/\/[^\s)]+)/g;

const linkify = (text: string): React.ReactNode[] =>
  text
    .split(URL_PATTERN)
    .map((segment, index) =>
      /^https?:\/\//.test(segment) ? (
        <a key={index} href={segment} target="_blank" rel="noreferrer">
          {segment}
        </a>
      ) : (
        segment
      )
    );

const ChatAssistant: React.FC = () => {
  const { getIdTokenClaims } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = messagesRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, status]);

  const appendMessage = (role: ChatRole, text: string): number => {
    const id = nextMessageId();
    setMessages((previous) => [...previous, { id, role, text }]);
    return id;
  };

  const upsertAgentMessage = (id: number, text: string) => {
    setMessages((previous) =>
      previous.some((message) => message.id === id)
        ? previous.map((message) =>
            message.id === id ? { ...message, text } : message
          )
        : [...previous, { id, role: "agent", text }]
    );
  };

  const handleEvent = (
    event: AdkEvent,
    live: { id: number; text: string }
  ): void => {
    if (event.errorMessage) {
      throw new Error(event.errorMessage);
    }
    const parts = event.content?.parts ?? [];
    for (const part of parts) {
      if (part.functionCall) {
        setStatus("Looking up the data…");
      } else if (part.functionResponse) {
        setStatus("Preparing the answer…");
      }
    }
    const eventText = parts
      .filter((part) => part.text)
      .map((part) => part.text)
      .join("");
    if (!eventText) {
      return;
    }
    setStatus(null);
    if (event.partial) {
      live.text += eventText;
      upsertAgentMessage(live.id, live.text);
    } else {
      // A non-partial event carries the full text of the segment: replace
      // the streamed bubble and start a fresh one for the next segment
      // (e.g. text after a tool call).
      upsertAgentMessage(live.id, eventText);
      live.id = nextMessageId();
      live.text = "";
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || isSending) {
      return;
    }
    setInput("");
    setIsSending(true);
    appendMessage("user", text);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw as string;
      const userId = claims?.sub as string;
      if (!sessionIdRef.current) {
        sessionIdRef.current = await createChatSession(token, userId);
      }
      const live = { id: nextMessageId(), text: "" };
      for await (const event of streamAgentReply(
        token,
        userId,
        sessionIdRef.current,
        text
      )) {
        handleEvent(event, live);
      }
    } catch (error: any) {
      // Drop the session: if the agent instance was recycled, the next
      // message starts a fresh conversation instead of failing forever.
      sessionIdRef.current = null;
      appendMessage(
        "error",
        `Something went wrong: ${error?.message ?? "unknown error"}. Please try again.`
      );
    } finally {
      setStatus(null);
      setIsSending(false);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className={Styles.page}>
      <div className={Styles.chatWindow}>
        <h2 className={Styles.heading}>OC Analyst</h2>
        <div className={Styles.subHeading}>
          Ask questions about order communication data in plain language.
        </div>
        <div className={Styles.messages} ref={messagesRef}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={
                message.role === "user"
                  ? Styles.userBubble
                  : message.role === "error"
                  ? Styles.errorBubble
                  : Styles.agentBubble
              }
            >
              {message.role === "agent" ? linkify(message.text) : message.text}
            </div>
          ))}
          {status ? <div className={Styles.status}>{status}</div> : null}
        </div>
        <div className={Styles.inputRow}>
          <input
            className={Styles.input}
            value={input}
            placeholder="e.g. How many notifications were sent in Sweden last week?"
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onInputKeyDown}
            disabled={isSending}
          />
          <button
            className={Styles.sendBtn}
            onClick={send}
            disabled={isSending || !input.trim()}
          >
            {isSending ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatAssistant;
